/* RESPONSIBLE TEAM: team-standalone */
import type ZendeskConfiguration from 'embercom/models/standalone/zendesk-configuration';
import Platform from './platform';
import type IntercomMessengerConfiguration from 'embercom/models/standalone/intercom-messenger-configuration';
import { IntegrationState, RESPONSE_PROVIDED_ERRORS, StandalonePlatform } from '../constants';
import type ApplicationInstance from '@ember/application/instance';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { inject as service } from '@ember/service';
import { ZendeskConversationExtractionRow } from './component-data/conversation-extraction-row-data';
import ZendeskKnowledgeHubModalData from './component-data/zendesk/knowledge-hub-modal';
import ZendeskConversationExcerpt from './component-data/zendesk/conversation-excerpt';
import ZendeskOnboardingGuideStep from './component-data/zendesk/onboarding-guide-step';
import ZendeskRequestAccessData from './component-data/zendesk/request-access';
import ZendeskConversationIngestion from './component-data/zendesk/conversation-ingestion';
import { tracked } from '@glimmer/tracking';
import ZendeskDataLoader from 'embercom/services/fin-standalone-service/zendesk-data-loader';
import { type SunshineChannel, type ZendeskBrand } from 'embercom/objects/standalone/zendesk';
import {
  ZendeskDataFields,
  type ZendeskTicketsSetupData,
} from 'embercom/objects/standalone/data-fields/zendesk';
import type OfficeHoursSchedule from 'embercom/models/office-hours-schedule';
import { taskFor } from 'ember-concurrency-ts';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type AppService from 'embercom/services/app-service';
import ZendeskChannelTargetingBodyData from './component-data/zendesk/channel-targeting-body-data';
import { ZendeskAiContentLibraryData } from './component-data/ai-content-library';
import { ZendeskKnowledgeContentData } from './component-data/knowlege-content-data';
import { ZendeskComposerOverflowMenuTooltipData } from './component-data/overflow-menu-tooltip-data';
import { ZendeskExampleCodeSnippetData } from './component-data/example-code-snippet-data';

export default class ZendeskPlatform extends Platform {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare store: Store;

  intercomMessengerConfig?: IntercomMessengerConfiguration;

  /* Zendesk specific */
  zendeskDataLoader: ZendeskDataLoader;
  @tracked zendeskConfig?: ZendeskConfiguration;
  @tracked zendeskBrands: ZendeskBrand[] = [];
  @tracked sunshineChannels: SunshineChannel[] = [];
  @tracked zendeskTicketsSetupData: ZendeskTicketsSetupData = {
    admins: [],
    email_addresses: [],
    live_trigger: { id: undefined },
  };
  @tracked zendeskDataFields: ZendeskDataFields = new ZendeskDataFields({
    userFields: [],
    ticketFields: [],
    organizationFields: [],
  });
  @tracked zendeskSchedules: Array<OfficeHoursSchedule> = [];

  constructor(
    owner: ApplicationInstance,
    inputs: {
      zendeskConfig?: ZendeskConfiguration;
      intercomMessengerConfig?: IntercomMessengerConfiguration;
    },
  ) {
    super(owner);
    this.zendeskConfig = inputs.zendeskConfig;
    this.zendeskDataLoader = new ZendeskDataLoader(this);
    this.intercomMessengerConfig = inputs.intercomMessengerConfig;
  }

  get componentData(): Platform['componentData'] {
    return {
      'conversation-extraction': new ZendeskConversationExtractionRow(this),
      'knowledge-hub-modal': new ZendeskKnowledgeHubModalData(this),
      'conversation-excerpt': new ZendeskConversationExcerpt(this),
      'onboarding-guide-step': new ZendeskOnboardingGuideStep(this),
      'request-access': new ZendeskRequestAccessData(this),
      'channel-targeting-body': new ZendeskChannelTargetingBodyData(),
      'ai-content-library': ZendeskAiContentLibraryData,
      'knowledge-content': ZendeskKnowledgeContentData,
      'composer-overflow-menu-tooltip': ZendeskComposerOverflowMenuTooltipData,
      'example-code-snippet': ZendeskExampleCodeSnippetData,
      'conversation-ingestion': new ZendeskConversationIngestion(this),
    } as const;
  }

  get supportsErrorLogs() {
    return this.appService.app.canAccessZendeskErrorLogs;
  }

  async loadConfigData() {
    if (taskFor(this.zendeskDataLoader.loadZendeskConfig).isRunning) {
      return taskFor(this.zendeskDataLoader.loadZendeskConfig).lastRunning!;
    } else {
      return taskFor(this.zendeskDataLoader.loadZendeskConfig).perform();
    }
  }

  async loadRequiredData() {
    await Promise.all([this.zendeskDataLoader.performLoads(), this.loadMessengerConfig()]);
  }

  async loadDataFields() {
    if (taskFor(this.zendeskDataLoader.loadZendeskDataFields).isRunning) {
      return taskFor(this.zendeskDataLoader.loadZendeskDataFields).lastRunning!;
    } else {
      return taskFor(this.zendeskDataLoader.loadZendeskDataFields).perform();
    }
  }

  async loadMessengerConfig() {
    this.intercomMessengerConfig = await this.store.findRecord(
      'standalone/intercom-messenger-configuration',
      this.appService.app.id,
    );
  }

  @action async saveZendeskConfig() {
    if (!this.zendeskConfig || !this.zendeskDataLoader) {
      throw new Error('zendeskConfig or zendeskDataLoader are not present.');
    }

    if (this.zendeskConfig) {
      this.zendeskConfig.subdomain = this.zendeskConfig.subdomain?.trim();
      this.zendeskConfig.username = this.zendeskConfig.username?.trim();
      this.zendeskConfig.token = this.zendeskConfig.token?.trim();

      if (this.zendeskConfig.sunshineIntegration) {
        this.zendeskConfig.sunshineIntegration.sunshineAppId =
          this.zendeskConfig.sunshineIntegration.sunshineAppId?.trim();
        this.zendeskConfig.sunshineIntegration.apiKeyId =
          this.zendeskConfig.sunshineIntegration.apiKeyId?.trim();
        this.zendeskConfig.sunshineIntegration.apiSecret =
          this.zendeskConfig.sunshineIntegration.apiSecret?.trim();
      }
    }

    try {
      await this.zendeskConfig.save();
      taskFor(this.zendeskDataLoader.loadZendeskTicketsSetupData).perform();
      taskFor(this.zendeskDataLoader.loadSunshineChannels).perform();
      taskFor(this.zendeskDataLoader.loadZendeskBrands).perform();
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.setup.api.save-error'),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
      throw e;
    }
  }

  get isLoadingZendeskTicketsSetupData() {
    return this.zendeskDataLoader?.isLoadingZendeskTicketsSetupData || false;
  }

  get isLoadingZendeskConfig() {
    return this.zendeskDataLoader?.isLoadingZendeskConfig || false;
  }

  get isLoadingZendeskSchedules() {
    return this.zendeskDataLoader?.isLoadingZendeskSchedules || false;
  }

  get isLoadingZendeskDataFields() {
    return this.zendeskDataLoader?.isLoadingZendeskDataFields || false;
  }
  get isConnected() {
    return this.zendeskConfig?.isConnected || false;
  }

  get hasInstalledCopilot() {
    return this.zendeskConfig?.hasInstalledCopilot || false;
  }

  get supportsHistoricalSyncing() {
    return true;
  }

  get historicalSyncingEnabled() {
    // TODO: SA Generic platform migration – This isConversationSyncingEnabled is kind of circular, move it to to the base object?
    return (
      (this.zendeskConfig?.isConnected && this.finStandaloneService.isConversationSyncingEnabled) ||
      false
    );
  }

  get finIsLive() {
    return (
      this.zendeskConfig?.isEnabledForTickets ||
      this.zendeskConfig?.sunshineIntegration?.isEnabledForSunshineMessenger ||
      this.intercomMessengerConfig?.isMessengerEnabled ||
      false
    );
  }

  get finIsPaused() {
    return (
      this.zendeskConfig?.isPausedForTickets ||
      this.zendeskConfig?.sunshineIntegration?.isPausedForSunshineMessenger ||
      this.intercomMessengerConfig?.isMessengerPaused ||
      false
    );
  }

  get connectionState() {
    return this.calculateHighestIntegrationState([
      this.zendeskConfig?.highestIntegrationState ?? IntegrationState.NotConfigured,
      this.intercomMessengerConfig?.integrationState ?? IntegrationState.NotConfigured,
    ]);
  }

  get icon() {
    return 'zendesk' as const;
  }

  get platformKey() {
    return StandalonePlatform.Zendesk;
  }

  get supportsFinPlayground() {
    return true;
  }

  get supportsConversationSyncing() {
    return true;
  }

  findMappedConversationField(id: string | undefined) {
    return this.zendeskDataFields.ticketFields.find((field) => field.intercom_cvda?.id === id);
  }

  isMappedUserAttribute(attributeIdentifier: string) {
    return this.zendeskDataFields.userFields.any(
      (userField) =>
        userField.intercom_cda?.identifier === attributeIdentifier ||
        userField.intercom_sda?.identifier === attributeIdentifier,
    );
  }

  isMappedCompanyAttribute(attributeIdentifier: string) {
    return this.zendeskDataFields.organizationFields.any(
      (organizationField) =>
        `company.${organizationField.intercom_cda?.identifier}` === attributeIdentifier ||
        `company.${organizationField.intercom_sda?.identifier}` === attributeIdentifier,
    );
  }

  isMappedConversationAttribute(attributeIdentifier: string) {
    return this.zendeskDataFields.ticketFields.any(
      (ticketField) => `conversation.${ticketField.intercom_cvda?.id}` === attributeIdentifier,
    );
  }

  get ticketFields() {
    return this.zendeskDataFields.ticketFields;
  }

  get userFields() {
    return this.zendeskDataFields.userFields;
  }

  get companyFields() {
    return this.zendeskDataFields.organizationFields;
  }
}
